import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchImportDataTypesThunk } from '../store/thunks/mdsThunks';
import { IMPORT_DATA_TYPE_TEMPLATES } from '../config/importDataTypeTemplates';

/**
 * Transforms raw API records from tbl_import_data_type_configs into
 * card-ready objects.
 * @param {Array} records
 * @returns {Array}
 */
const transformImportDataTypeResponse = (records) => {
    if (!Array.isArray(records)) return [];

    return records.map((record) => {
        let config = record.config;
        if (typeof config === 'string') {
            try {
                config = JSON.parse(config);
            } catch (e) {
                config = {};
            }
        }
        config = config || {};

        return {
            key: record.import_type_key,
            label: config.label || record.import_type_key,
            displayOrder: parseInt(record.display_order, 10) || 0,
            isVisible: record.is_visible !== false,
            isEnabled: record.is_enabled !== false,
            iconConfig: config.iconConfig || {},
            description: config.description || '',
            acceptedFormats: config.acceptedFormats || [],
            actionConfig: config.actionConfig || {},
        };
    });
};

/**
 * Custom hook for fetching import data type configs from Supabase.
 * Uses a thunk (like UserManualsPage) — state is local, no Redux slice.
 *
 * @param {Object} options
 * @param {Array}  options.fallbackTypes - Override default fallback list
 *
 * @returns {{ types: Array, loading: boolean, error: string|null, refetch: Function }}
 */
export function useImportDataTypes(options = {}) {
    const { fallbackTypes } = options;

    const dispatch = useDispatch();
    const domainId = useSelector((state) => state.domainConfig?.data?.domain_id);

    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fallback = fallbackTypes || IMPORT_DATA_TYPE_TEMPLATES;

    const fetchData = useCallback(async (signal) => {
        if (!domainId) {
            setTypes(fallback);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const promiseRef = dispatch(fetchImportDataTypesThunk({ domainId }));

        // Allow external abort (e.g. unmount) to cancel the thunk
        signal?.addEventListener('abort', () => promiseRef.abort());

        const result = await promiseRef;

        if (fetchImportDataTypesThunk.fulfilled.match(result)) {
            const records = result?.payload?.data?.records || [];
            const transformed = transformImportDataTypeResponse(records);

            const visible = transformed
                .filter((t) => t.isVisible !== false)
                .sort((a, b) => a.displayOrder - b.displayOrder);

            setTypes(visible.length > 0 ? visible : fallback);
        } else if (fetchImportDataTypesThunk.rejected.match(result)) {
            if (result?.payload !== 'cancelled') {
                setError(result?.payload || 'Failed to load import data types');
                setTypes(fallback);
            }
        }

        setLoading(false);
    }, [dispatch, domainId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const controller = new AbortController();
        fetchData(controller.signal);
        return () => controller.abort();
    }, [fetchData]);

    const refetch = useCallback(() => {
        fetchData();
    }, [fetchData]);

    return { types, loading, error, refetch };
}

export { transformImportDataTypeResponse };
